import React, { createContext, useState, useContext, useEffect } from 'react';
import { useCourseUIStore } from '../store/useCourseUIStore';

// 1. 修改 User 类型，增加 role 和 🔥 avatar_url 字段
type User = {
  id: number;
  username: string;
  email: string;
  name: string;
  role: string; 
  avatar_url?: string;
  memo?: string;
} | null;

// AuthContext类型定义
type AuthContextType = {
  user: User;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string, rememberMe?: boolean) => Promise<User | null>;
  faceLogin: (file: File, rememberMe?: boolean) => Promise<User | null>;
  logout: () => void;
  fetchUser: (explicitToken?: string) => Promise<void>;
  updateUserAvatar: (url: string) => void;
};

// 创建Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider组件
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 辅助函数：获取token，优先从sessionStorage获取，然后从localStorage（带过期时间）获取
  const getToken = (): string | null => {
    // 先从sessionStorage获取
    const sessionToken = sessionStorage.getItem('token');
    if (sessionToken) {
      return sessionToken;
    }
    // 再从localStorage（带过期时间）获取
    return getWithExpiry('token');
  };

  // 辅助函数：设置带过期时间的localStorage
  const setWithExpiry = (key: string, value: string, ttl: number): void => {
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + ttl,
    };
    localStorage.setItem(key, JSON.stringify(item));
  };

  // 辅助函数：获取带过期时间的localStorage
  const getWithExpiry = (key: string): string | null => {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) {
      return null;
    }
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  };

  const [user, setUser] = useState<User>(null);
  const [token, setToken] = useState<string | null>(getToken());
  const [loading, setLoading] = useState<boolean>(true);

  // 登录函数
  const login = async (username: string, password: string, rememberMe: boolean = false): Promise<User | null> => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
    const response = await fetch(`${baseUrl}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      // 读取服务端错误详情并抛出，让调用方（Login.tsx）能展示真实错误而非泛化的"身份不匹配"
      let detail = '登录失败';
      try {
        const errData = await response.json();
        detail = errData.detail || errData.message || detail;
      } catch {
        try { detail = (await response.text()) || detail; } catch { /* ignore */ }
      }
      const err: any = new Error(detail);
      err.response = { status: response.status, data: { detail } };
      throw err;
    }

    const data = await response.json();
    const token = data.access_token;
    if (!token) return null;

    if (rememberMe) {
      // 记住我：存储15天
      setWithExpiry('token', token, 15 * 24 * 60 * 60 * 1000);
    } else {
      // 不记住我：会话存储
      sessionStorage.setItem('token', token);
    }

    // 先设置token状态
    setToken(token);

    // 直接获取用户信息并返回，不依赖异步状态更新
    try {
      const userResponse = await fetch(`${baseUrl}/auth/me/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        // 更新user状态
        setUser(userData);
        // 直接返回获取到的用户数据
        return userData;
      }
    } catch (userError) {
      console.error('获取用户信息失败:', userError);
    }

    // 获取用户信息失败，清除token
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
    return null;
  };

  const handleFaceLogin = async (file: File, rememberMe: boolean = false): Promise<User | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'}/face/login/`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const token = data.access_token;
        
        if (rememberMe) {
          setWithExpiry('token', token, 15 * 24 * 60 * 60 * 1000);
        } else {
          sessionStorage.setItem('token', token);
        }
        
        setToken(token);
        
        try {
          const userResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'}/auth/me/`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUser(userData);
            return userData;
          } else {
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
            setToken(null);
            setUser(null);
            return null;
          }
        } catch (userError) {
          console.error('获取用户信息失败:', userError);
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          setToken(null);
          setUser(null);
          return null;
        }
      }
      return null;
    } catch (error) {
      console.error('人脸登录失败:', error);
      return null;
    }
  };

  // 获取当前用户信息
  const fetchUser = async (explicitToken?: string): Promise<void> => {
    const currentToken = explicitToken || token;
    if (!currentToken) {
      setLoading(false); // 没有 token 时也要结束 loading 状态
      return;
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'}/auth/me/`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        // 🛡️ 务必确认 userData 包含 role 字段
        setUser(userData);
      } else {
        // 如果获取用户信息失败，清除token
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false); // 无论成功失败，请求结束都关闭 loading
    }
  };

  // 登出函数
  const logout = (): void => {
    // 同时清除sessionStorage和localStorage中的token
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    
    // 清除课程UI缓存
    localStorage.removeItem('nexus-course-ui-storage');
    
    // 清除全局选中的教案版本ID
    localStorage.removeItem('globalSelectedPlanId');
    
    // 重置状态
    setToken(null);
    setUser(null);
  };

  // 🔥 [Vibe Feature] 手动更新头像 (无需重新请求API)
  const updateUserAvatar = (url: string) => {
    if (user) {
      setUser({ ...user, avatar_url: url });
    }
  };

  // 初始加载时获取用户信息
  useEffect(() => {
    fetchUser();
  }, [token]);

  // Context值
  const contextValue: AuthContextType = {
    user,
    token,
    loading,
    login,
    faceLogin: handleFaceLogin,
    logout,
    fetchUser,
    updateUserAvatar,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义Hook，方便组件使用AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
};
